import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  Signal,
} from '@angular/core';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { map, Observable, Subject, Subscription } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Params, RouterModule } from '@angular/router';
// Components

import { getTranslatedURI } from '@app/shared/utils/url.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { AkitaNewsletterService } from '@app/akita/api/newsletter/state/newsletter.service';
import { logClickOnBuyback } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';

const FIREBASE_BANNER_FOLDER =
  'https://firebasestorage.googleapis.com/v0/b/popsygae.appspot.com/o/HomePageBanners%2FTradeInBanner%2F';
const FIREBASE_BANNER_FOLDER_2 =
  '.png?alt=media&token=829995d9-4e62-4320-96f9-c76105a876b9';

@Component({
  selector: 'app-coming-soon-banner',
  templateUrl: './coming-soon-banner.template.html',
  styleUrls: ['./coming-soon-banner.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule, RouterModule],
})
export class ComingSoonBannerComponent implements OnDestroy {
  @Input() public country?: string | null;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout: Signal<boolean>;

  public queryParamsToKeep$: Observable<Params>;

  public bannerLink: Signal<string | null>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly translateService: TranslocoService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaNewsletterService: AkitaNewsletterService,
    private readonly analytics: GoogleAnalyticsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();

    this.isRtlLayout = toSignal(this.akitaRouterQuery.selectIsRtlLayout(), {
      initialValue: false,
    });

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.bannerLink = toSignal(
      this.biggerThan$.pipe(
        map((biggerThan) => {
          const size = biggerThan.sm ? 'big' : 'medium';
          const lang = this.translateService.getActiveLang();
          return `${FIREBASE_BANNER_FOLDER}${size}_${lang}${FIREBASE_BANNER_FOLDER_2}`;
        })
      ),
      { initialValue: null }
    );
  }

  public get homeUrlFragment(): Array<string> {
    return this.akitaRouterQuery.homeUrlFragment;
  }

  public informationPageUrlFragment(path: string): Array<string> {
    const fragment = this.homeUrlFragment;
    const translatedPath = getTranslatedURI(
      path,
      this.translateService.getActiveLang() || 'en'
    );
    fragment.push(translatedPath);
    return fragment;
  }

  public openNewsletterDialog(): void {
    this.akitaNewsletterService.openUpNewsletter();
  }

  public logOpenBuyback(trigger: string): void {
    logClickOnBuyback(this.analytics.logEventWrapper, trigger);
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
